import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  DateFormatPref,
  getDateFormat,
  applyDateFormat,
  subscribeDateFormat,
  formatDate,
  formatDateLong,
  formatDateTime,
} from '../utils/dateFormat';
import api from '../services/api';

/**
 * DateFormatContext — T206 (a). His ruling: **ONE company-wide format, admin-only.**
 *
 * ⚠ WHAT CHANGED, AND IT IS THE WHOLE BUG:
 * this provider used to write `localStorage` and stop. The value never left the
 * browser, so no PDF could ever see it — his #84, exactly. It now READS the company
 * setting from the server on mount and WRITES it back through `PUT /settings/system`,
 * a route already gated `requireCoAdmin`. `localStorage` survives only as a
 * first-paint cache so a reload does not flash the wrong layout.
 *
 * Three rules this file keeps:
 *  1. **NEVER BLOCK THE APP.** The fetch is best-effort: unauthenticated, offline, or
 *     a 500 all leave the cached value standing. A date format is not worth a blank app.
 *  2. **NEVER WRITE THE CACHE BEFORE THE SERVER ACCEPTS.** Applying optimistically
 *     would show a Sales Engineer a layout their own documents do not use, after a 403
 *     they never saw. A screen that lies is worse than one that refuses.
 *  3. **A REFUSAL IS SURFACED, NOT SWALLOWED** (mistake shape 22). T6 was carried as
 *     unbuilt for fifteen sessions precisely because a 403 went into an empty catch.
 */

interface DateFormatContextValue {
  dateFormat: DateFormatPref;
  /** Persists to the SERVER. Resolves true on success, false if it was refused. */
  setDateFormat: (pref: DateFormatPref) => Promise<boolean>;
  /** True while a save is in flight. */
  saving: boolean;
  /** Why the last save failed, in words. Null when the last save succeeded. */
  error: string | null;
  formatDate: (input: Parameters<typeof formatDate>[0], fallback?: string) => string;
  formatDateLong: (input: Parameters<typeof formatDateLong>[0], fallback?: string) => string;
  formatDateTime: (input: Parameters<typeof formatDateTime>[0], fallback?: string) => string;
}

const DateFormatContext = createContext<DateFormatContextValue | undefined>(undefined);

const isValidPref = (v: unknown): v is DateFormatPref =>
  v === 'MM/DD/YYYY' || v === 'DD/MM/YYYY';

export const DateFormatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pref, setPref] = useState<DateFormatPref>(() => getDateFormat());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetched = useRef(false);

  useEffect(() => subscribeDateFormat(setPref), []);

  /* Hydrate from the company setting. Guarded on a token because this provider wraps
     the login page too, and an unauthenticated GET would 401 on every visit. */
  useEffect(() => {
    if (fetched.current) return;
    let token: string | null = null;
    try { token = window.localStorage.getItem('token'); } catch { token = null; }
    if (!token) return;
    fetched.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/settings/system');
        const value = (res as any)?.data?.data?.dateFormat ?? (res as any)?.data?.dateFormat;
        if (!cancelled && isValidPref(value)) {
          applyDateFormat(value);
          setPref(value);
        }
      } catch {
        /* Rule 1 — the cached value stands. Never block the app on this. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = useCallback(async (next: DateFormatPref): Promise<boolean> => {
    if (!isValidPref(next)) return false;
    setSaving(true);
    setError(null);
    try {
      /* ONLY this key is sent. `updateSystemSettings` merges onto the stored bag, so
         posting the whole form back would let this one control overwrite every other
         system setting another screen had just changed. */
      await api.put('/settings/system', { dateFormat: next });
      /* Rule 2 — the cache is written only now, after the server accepted it. */
      applyDateFormat(next);
      setPref(next);
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      const fromServer = err?.response?.data?.message;
      setError(
        status === 403
          ? 'Only an administrator can change the date format for the company.'
          : (fromServer || 'The date format could not be saved. It is unchanged for everyone.')
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const value = useMemo<DateFormatContextValue>(() => ({
    dateFormat: pref,
    setDateFormat: update,
    saving,
    error,
    // Re-bind helpers to the current pref so components re-render on a change.
    formatDate: (input, fallback) => formatDate(input, fallback),
    formatDateLong: (input, fallback) => formatDateLong(input, fallback),
    formatDateTime: (input, fallback) => formatDateTime(input, fallback),
  }), [pref, update, saving, error]);

  return <DateFormatContext.Provider value={value}>{children}</DateFormatContext.Provider>;
};

export const useDateFormat = (): DateFormatContextValue => {
  const ctx = useContext(DateFormatContext);
  if (!ctx) throw new Error('useDateFormat must be used within DateFormatProvider');
  return ctx;
};
